import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tag } from 'lucide-react';
import { useOffers } from '../hooks/useOffers';
import { OFFERS_CONFIG } from '../config/offersConfig';
import { formatTierLabel } from '../utils/tiers';
import { formatOfferValidity } from '../utils/dates';
import { getTierRank } from '../services/offersService';
import { trackOfferEvent } from '../services/analyticsService';

function OfferLogo({ brandName, brandLogoUrl }) {
  const [failed, setFailed] = useState(false);
  const showImg = Boolean(brandLogoUrl) && !failed;

  return (
    <div className={`${OFFERS_CONFIG.logo.containerClassName} shrink-0 flex items-center justify-center`}>
      {showImg ? (
        <img
          src={brandLogoUrl}
          alt={`${brandName} logo`}
          onError={() => setFailed(true)}
          className={OFFERS_CONFIG.logo.imgClassName}
          loading="lazy"
        />
      ) : (
        <div className="h-full w-full rounded-md bg-gray-100 text-gray-500 text-xs flex items-center justify-center">
          {OFFERS_CONFIG.logo.fallbackText}
        </div>
      )}
    </div>
  );
}

function OfferCard({ offer, userTier, userId, impressionSet }) {
  const userRank = getTierRank(userTier);
  const requiredRank = getTierRank(offer.tierRequired);
  const unlocked = requiredRank === 0 || userRank >= requiredRank;
  const [codeRevealed, setCodeRevealed] = useState(false);
  const codeViewTrackedRef = useRef(false);

  useEffect(() => {
    if (!offer?.id) return;
    if (!impressionSet?.current) return;
    if (impressionSet.current.has(offer.id)) return;
    impressionSet.current.add(offer.id);
    trackOfferEvent({
      event_type: 'impression',
      offer_id: offer.id,
      brand: offer.brandName,
      user_id: userId,
      tier_at_time: userTier ?? null,
      page_context: 'rewards',
    });
  }, [offer?.id, offer?.brandName, impressionSet, userId, userTier]);

  const tierLabel = offer.tierRequired
    ? formatTierLabel(offer.tierRequired)
    : OFFERS_CONFIG.unknownTierLabel;
  const validity = formatOfferValidity({ startDate: offer.startDate, endDate: offer.endDate });

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-3 min-w-0">
          <OfferLogo brandName={offer.brandName} brandLogoUrl={offer.brandLogoUrl} />
          <div className="min-w-0">
            <h3 className="font-semibold text-lg leading-snug">{offer.offerTitle}</h3>
            <p className="text-blue-600 font-medium text-sm truncate">{offer.brandName}</p>
            {offer.campaignLabel ? (
              <p className="text-xs text-indigo-700 font-medium mt-0.5">{offer.campaignLabel}</p>
            ) : null}
            {offer.category ? <p className="text-xs text-gray-500 mt-0.5">{offer.category}</p> : null}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          {offer.isFeatured ? (
            <span className="text-xs font-semibold uppercase px-2 py-1 rounded-full bg-purple-100 text-purple-900">
              {OFFERS_CONFIG.featuredBadgeText}
            </span>
          ) : null}
          <span className="text-xs font-semibold uppercase px-2 py-1 rounded-full bg-amber-100 text-amber-900">
            {tierLabel}
          </span>
        </div>
      </div>

      {offer.offerDescription ? <p className="text-gray-600 text-sm">{offer.offerDescription}</p> : null}

      {!unlocked && offer.tierRequired ? (
        <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3">
          <p className="text-sm font-medium text-gray-700">
            Unlock at {formatTierLabel(offer.tierRequired)} tier
          </p>
        </div>
      ) : null}

      {unlocked && offer.discountCode ? (
        <div className="mt-3 rounded-lg bg-green-50 border border-green-100 p-3">
          <p className="text-xs text-gray-600 mb-2">Discount code</p>
          {codeRevealed ? (
            <p className="font-mono text-lg font-semibold text-green-800 tracking-wide break-all">
              {offer.discountCode}
            </p>
          ) : (
            <button
              type="button"
              onClick={() => {
                setCodeRevealed(true);
                if (!codeViewTrackedRef.current) {
                  codeViewTrackedRef.current = true;
                  trackOfferEvent({
                    event_type: 'code_view',
                    offer_id: offer.id,
                    brand: offer.brandName,
                    user_id: userId,
                    tier_at_time: userTier ?? null,
                    page_context: 'rewards',
                  });
                }
              }}
              className="w-full px-4 py-2 bg-white border border-green-200 rounded-lg text-green-800 font-medium hover:bg-green-50"
            >
              Reveal code
            </button>
          )}
        </div>
      ) : null}

      {unlocked && offer.ctaLink ? (
        <a
          href={offer.ctaLink}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            trackOfferEvent({
              event_type: 'click',
              offer_id: offer.id,
              brand: offer.brandName,
              user_id: userId,
              tier_at_time: userTier ?? null,
              page_context: 'rewards',
            });
          }}
          className="mt-3 inline-flex items-center justify-center w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
        >
          {offer.ctaText || OFFERS_CONFIG.defaultCtaText}
        </a>
      ) : null}

      {!unlocked && offer.ctaLink ? (
        <button
          type="button"
          disabled
          className="mt-3 inline-flex items-center justify-center w-full px-4 py-2 bg-gray-200 text-gray-500 rounded-lg font-medium cursor-not-allowed"
        >
          {offer.ctaText || OFFERS_CONFIG.defaultCtaText}
        </button>
      ) : null}

      {validity ? <p className="text-xs text-gray-400 mt-3">Valid: {validity}</p> : null}
    </div>
  );
}

const RewardsView = ({ tierStats, user }) => {
  const tier = tierStats?.tier;
  const { offers, categories, status, errorMessage } = useOffers(tier, {
    country: user?.country ?? null,
    communityId: user?.communityId ?? null,
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const impressionSet = useRef(new Set());
  const userId = user?.id ?? null;

  const visibleOffers = useMemo(() => {
    if (selectedCategory === 'all') return offers;
    return offers.filter((o) => o.category === selectedCategory);
  }, [offers, selectedCategory]);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-2">Rewards</h2>
        <p className="text-gray-600">
          Your tier is based on verified climate actions over the last 7 days. Unverified actions don&apos;t count until
          approved.
        </p>
      </div>

      <div className="rounded-xl border border-green-200 bg-green-50 p-4">
        <p className="font-semibold text-green-900">Current tier: {tierStats?.tierLabel ?? 'No tier'}</p>
        <p className="text-sm text-green-800 mt-1">
          Verified actions (last 7 days): {tierStats?.verifiedCountLast7Days ?? 0} — Gold 5+, Silver 3+, Bronze 1+
        </p>
      </div>

      <section>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Tag className="h-5 w-5 text-gray-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Offers</h3>
              <p className="text-sm text-gray-500">Offers update automatically without redeploys.</p>
            </div>
          </div>

          {categories.length > 0 ? (
            <label className="text-sm text-gray-700">
              <span className="sr-only">Filter by category</span>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
              >
                <option value="all">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        {status === 'loading' ? (
          <div className="text-sm text-gray-600">Loading offers…</div>
        ) : status === 'error' ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-800">{OFFERS_CONFIG.errorStateText}</p>
            {errorMessage ? <p className="text-xs text-red-700 mt-1">{errorMessage}</p> : null}
          </div>
        ) : visibleOffers.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-sm text-gray-600">{OFFERS_CONFIG.emptyStateText}</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {visibleOffers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                userTier={tier}
                userId={userId}
                impressionSet={impressionSet}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default RewardsView;
